import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { createMercadoPagoPayment, createTransaction } from '../../api';
import { deviceSessionId } from '@/shared/mercadopago';
import type { ThreeDSInfo } from '../../types';

const STATUS_DETAIL_MESSAGE_MAP: Record<string, string> = {
  cc_rejected_bad_filled_card_number: 'wallet.payment.rejected.badCardNumber',
  cc_rejected_bad_filled_date: 'wallet.payment.rejected.badDate',
  cc_rejected_bad_filled_other: 'wallet.payment.rejected.badOther',
  cc_rejected_bad_filled_security_code: 'wallet.payment.rejected.badSecurityCode',
  cc_rejected_blacklist: 'wallet.payment.rejected.blacklist',
  cc_rejected_call_for_authorize: 'wallet.payment.rejected.callForAuthorize',
  cc_rejected_card_disabled: 'wallet.payment.rejected.cardDisabled',
  cc_rejected_duplicated_payment: 'wallet.payment.rejected.duplicatedPayment',
  cc_rejected_high_risk: 'wallet.payment.rejected.highRisk',
  cc_rejected_insufficient_amount: 'wallet.payment.rejected.insufficientAmount',
  cc_rejected_max_attempts: 'wallet.payment.rejected.maxAttempts',
  cc_rejected_other_reason: 'wallet.payment.rejected.otherReason',
};

interface PaymentBrickFormData {
  formData: {
    transaction_amount: number;
    token: string;
    installments: number;
    payment_method_id: string;
    payer: {
      email: string;
      identification: {
        type: string;
        number: string;
      };
    };
  };
}

interface UseMercadoPagoPaymentOptions {
  amountCents: number;
  scheduleId?: string;
  onSuccess: () => void;
}

function buildPaymentParams(transactionId: number, formData: PaymentBrickFormData) {
  const { formData: data } = formData;
  const deviceId = deviceSessionId();

  return {
    transaction_id: transactionId,
    token: data.token,
    installments: data.installments,
    payment_method_id: data.payment_method_id,
    payer: data.payer,
    ...(deviceId && { device_id: deviceId }),
  };
}

function rejectionMessageId(statusDetail: string | undefined): string {
  return STATUS_DETAIL_MESSAGE_MAP[statusDetail ?? ''] ?? 'wallet.payment.failed';
}

export function useMercadoPagoPayment({ amountCents, scheduleId, onSuccess }: UseMercadoPagoPaymentOptions) {
  const intl = useIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challengeData, setChallengeData] = useState<ThreeDSInfo | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState<number | null>(null);

  const submit = useCallback(async (formData: PaymentBrickFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      let response;
      try {
        const transactionResponse = await createTransaction(amountCents, { scheduleId });
        setTransactionId(transactionResponse.transaction_id);
        if (transactionResponse.gateway !== 'mercadopago') {
          throw new Error('Unexpected gateway type');
        }

        const params = buildPaymentParams(transactionResponse.transaction_id, formData);
        response = await createMercadoPagoPayment(params);
      } catch {
        setError(intl.formatMessage({ id: 'wallet.payment.failed' }));
        throw new Error(intl.formatMessage({ id: 'wallet.payment.failed' }));
      }

      if (response.status_detail === 'pending_challenge' && response.three_ds_info) {
        setPaymentId(response.payment_id);
        setChallengeData(response.three_ds_info);
        return;
      }

      if (response.status === 'approved') {
        onSuccess();
        return;
      }

      if (response.status === 'pending' || response.status === 'in_process') {
        setPaymentId(response.payment_id);
        return;
      }

      const errorMsg = intl.formatMessage({ id: rejectionMessageId(response.status_detail) });
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  }, [amountCents, scheduleId, onSuccess, intl]);

  const clearError = useCallback(() => setError(null), []);

  return { submit, isSubmitting, error, clearError, challengeData, paymentId, transactionId };
}
